import { calyxConfig } from '@/utils/environment';
import { HeaderBuilder } from '@platform/http/blocs/services/header-builder';

export interface HttpOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/**
 * HTTP client for Calyx API requests.
 * Automatically includes authentication, business context headers, and base URL.
 *
 * @example
 * // Pass just the path, not the full URL
 * CalyxClient.get('/business-organizations/uuid/microproducts/config')
 */
export class CalyxClientImpl {
  private getUrl(path: string): string {
    return `${calyxConfig.apiUrl}${path}`;
  }

  async get<T>(path: string, options?: HttpOptions): Promise<T> {
    return this.request<T>(path, { method: 'GET', ...options });
  }

  async post<T>(
    path: string,
    body: unknown,
    options?: HttpOptions,
  ): Promise<T> {
    return this.requestWithBody<T>('POST', path, body, options);
  }

  async put<T>(path: string, body: unknown, options?: HttpOptions): Promise<T> {
    return this.requestWithBody<T>('PUT', path, body, options);
  }

  async delete<T>(path: string, options?: HttpOptions): Promise<T> {
    return this.request<T>(path, { method: 'DELETE', ...options });
  }

  private async requestWithBody<T>(
    method: 'POST' | 'PUT',
    path: string,
    body: unknown,
    options?: HttpOptions,
  ): Promise<T> {
    const headers = await HeaderBuilder.create()
      .withAuth()
      .withContentType()
      .build();

    const response = await fetch(this.getUrl(path), {
      body: JSON.stringify(body),
      method,
      ...options,
      headers: {
        ...headers,
        ...options?.headers,
      },
    });

    if (!response.ok) {
      throw new HttpError(
        `Request failed with status ${response.status}`,
        response.status,
      );
    }

    return response.json() as Promise<T>;
  }

  private async request<T>(
    path: string,
    options?: RequestInit & HttpOptions,
  ): Promise<T> {
    const headers = await HeaderBuilder.create().withAuth().build();

    const response = await fetch(this.getUrl(path), {
      ...options,
      headers: {
        ...headers,
        ...options?.headers,
      },
    });

    if (!response.ok) {
      throw new HttpError(
        `Request failed with status ${response.status}`,
        response.status,
      );
    }

    return response.json() as Promise<T>;
  }
}

/**
 * Singleton instance for use across the application.
 */
export const CalyxClient = new CalyxClientImpl();
